import { useId } from "react";

/**
 * Inline-SVG country flags for the language switcher.
 *
 * Windows system fonts (used by Chrome/Edge on Windows) do NOT include
 * country-flag emoji, so "🇦🇿" renders as its two regional-indicator letters
 * ("AZ"). Rendering flags as SVG instead guarantees identical output on every
 * OS/browser. Codes map to language codes, not country codes (en → UK flag).
 */
const Flag = ({ code, size = 16, className, title }) => {
    const clipId = useId();

    // Uniform 1.6:1 box for a compact chip. AZ/UK (officially 2:1) are slightly
    // narrowed via preserveAspectRatio="none"; RU is pure horizontal stripes so the
    // stretch is invisible. Equal widths keep the dropdown list aligned.
    const box = {
        display: "inline-block",
        width: size * 1.6,
        height: size,
        borderRadius: 2,
        overflow: "hidden",
        // Hairline so white-edged flags (UK, RU) read on light backgrounds.
        boxShadow: "inset 0 0 0 0.5px rgba(0,0,0,0.18)",
        flexShrink: 0,
        verticalAlign: "middle",
    };

    let svg = null;

    if (code === "az") {
        svg = (
            <svg viewBox="0 0 60 30" width="100%" height="100%" preserveAspectRatio="none" aria-hidden="true">
                <rect width="60" height="10" fill="#0092BC" />
                <rect y="10" width="60" height="10" fill="#EF3340" />
                <rect y="20" width="60" height="10" fill="#509E2F" />
                <circle cx="27" cy="15" r="5" fill="#fff" />
                <circle cx="29" cy="15" r="4" fill="#EF3340" />
                <g fill="#fff" transform="translate(36 15)">
                    <rect x="-2" y="-2" width="4" height="4" />
                    <rect x="-2" y="-2" width="4" height="4" transform="rotate(45)" />
                </g>
            </svg>
        );
    } else if (code === "ru") {
        svg = (
            <svg viewBox="0 0 9 6" width="100%" height="100%" preserveAspectRatio="none" aria-hidden="true">
                <rect width="9" height="6" fill="#fff" />
                <rect y="2" width="9" height="2" fill="#0039A6" />
                <rect y="4" width="9" height="2" fill="#D52B1E" />
            </svg>
        );
    } else if (code === "en") {
        svg = (
            <svg viewBox="0 0 60 30" width="100%" height="100%" preserveAspectRatio="none" aria-hidden="true">
                <clipPath id={clipId}>
                    <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
                </clipPath>
                <rect width="60" height="30" fill="#012169" />
                <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
                <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#${clipId})`} stroke="#C8102E" strokeWidth="4" />
                <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
                <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
            </svg>
        );
    }

    if (!svg) return null;

    return (
        <span className={className} style={box} role="img" aria-label={title || code} title={title}>
            {svg}
        </span>
    );
};

export default Flag;
